// src/components/Assets/AssetExpenses/index.js
// Asset expenses list with filtering and totals

import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FaPlus, FaFilter, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import useAssetExpenses from "../hooks/useAssetExpenses";
import useAssets from "../hooks/useAssets";
import ExpenseCard from "./ExpenseCard";
import AddExpenseForm from "./AddExpenseForm";
import "../style.css";
import "./ExpenseStyles.css";

const AssetExpenses = () => {
  const [t, { language }] = useTranslation();
  const location = useLocation();
  const { expenses, loading: expensesLoading, addExpense } = useAssetExpenses();
  const { assets, loading: assetsLoading } = useAssets();

  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preSelectedAsset, setPreSelectedAsset] = useState(null);

  // Filter and sort states
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("newest"); // "newest" or "oldest"

  // Check for navigation state from AssetsList
  useEffect(() => {
    if (location.state?.openForm && location.state?.selectedAsset) {
      setPreSelectedAsset(location.state.selectedAsset);
      setShowForm(true);
      // Clear the state to prevent re-opening on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Get asset name by ID
  const getAssetName = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.name : t("Unknown Asset");
  };

  // Filter expenses based on selected filters
  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];

    // Filter by asset - handle nested payment structure
    if (selectedAssetId) {
      filtered = filtered.filter(exp => {
        const payment = exp.payment || exp;
        return payment.assetId === parseInt(selectedAssetId, 10);
      });
    }

    // Filter by date range - handle nested payment structure
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0); // Start of day
      filtered = filtered.filter(exp => {
        const payment = exp.payment || exp;
        const expenseDate = typeof payment.paymentDate === 'number'
          ? payment.paymentDate
          : new Date(payment.paymentDate).getTime();
        return expenseDate >= start.getTime();
      });
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(exp => {
        const payment = exp.payment || exp;
        const expenseDate = typeof payment.paymentDate === 'number'
          ? payment.paymentDate
          : new Date(payment.paymentDate).getTime();
        return expenseDate <= end.getTime();
      });
    }

    // Apply sorting - handle nested payment structure
    filtered.sort((a, b) => {
      const paymentA = a.payment || a;
      const paymentB = b.payment || b;
      const dateA = typeof paymentA.paymentDate === 'number'
        ? paymentA.paymentDate
        : new Date(paymentA.paymentDate).getTime();
      const dateB = typeof paymentB.paymentDate === 'number'
        ? paymentB.paymentDate
        : new Date(paymentB.paymentDate).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [expenses, selectedAssetId, startDate, endDate, sortOrder]);

  // Calculate total
  const total = useMemo(() => {
    return filteredExpenses.reduce((sum, exp) => {
      const payment = exp.payment || exp;
      return sum + (payment.amountPaid || 0);
    }, 0);
  }, [filteredExpenses]);

  // Format currency for total - Always use dot as decimal separator
  const formatCurrency = (value) => {
    const formatted = parseFloat(value).toFixed(2);
    // Add thousand separators while keeping dot as decimal separator
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Handle form submission
  const handleFormSubmit = async (expenseData) => {
    setIsSubmitting(true);
    const success = await addExpense(expenseData);  // assetId is already in expenseData
    setIsSubmitting(false);

    if (success) {
      setShowForm(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedAssetId("");
    setStartDate("");
    setEndDate("");
  };

  // Check if any filter is active
  const hasActiveFilters = selectedAssetId || startDate || endDate;

  // Loading state
  if (expensesLoading || assetsLoading) {
    return (
      <div className="assets-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-container">
      {/* Header */}
      <div className="assets-header">
        <div>
          <h2>{t("Asset Expenses")}</h2>
          <p className="subtitle">{t("Track and manage asset-related expenses")}</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          <FaPlus /> {t("Add Expense")}
        </button>
      </div>

      {/* Filters Section */}
      <div className="expenses-filters">
        <div className="filters-header">
          <div className="filters-title">
            <FaFilter /> {t("Filters")}
          </div>
          {hasActiveFilters && (
            <button
              className="btn-clear-filters"
              onClick={clearFilters}
            >
              {t("Clear Filters")}
            </button>
          )}
        </div>

        <div className="filters-row">
          {/* Asset Filter */}
          <div className="filter-group">
            <label className="filter-label">{t("Asset")}</label>
            <select
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
              className="filter-input"
            >
              <option value="">{t("All Assets")}</option>
              {assets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date Filter */}
          <div className="filter-group">
            <label className="filter-label">{t("From Date")}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* End Date Filter */}
          <div className="filter-group">
            <label className="filter-label">{t("To Date")}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Sort Order Toggle */}
          <div className="filter-group">
            <label className="filter-label">{t("Sort By")}</label>
            <button
              className="sort-toggle-btn"
              onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
              title={t(sortOrder === "newest" ? "Showing newest first" : "Showing oldest first")}
            >
              {sortOrder === "newest" ? (
                <>
                  <FaSortAmountDown /> {t("Newest First")}
                </>
              ) : (
                <>
                  <FaSortAmountUp /> {t("Oldest First")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Total Summary */}
      {filteredExpenses.length > 0 && (
        <div className="expenses-summary">
          <div className="summary-item">
            <span className="summary-label">{t("Total Expenses")}:</span>
            <span className="summary-value">{formatCurrency(total)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">{t("Count")}:</span>
            <span className="summary-count">{filteredExpenses.length}</span>
          </div>
        </div>
      )}

      {/* Expenses List */}
      {filteredExpenses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">💰</div>
          <h3>
            {hasActiveFilters
              ? t("No expenses match the selected filters")
              : t("No Expenses Yet")}
          </h3>
          <p>
            {hasActiveFilters
              ? t("Try adjusting your filters")
              : t("Click 'Add Expense' to record your first expense")}
          </p>
        </div>
      ) : (
        <div className="expenses-list">
          {filteredExpenses.map(expense => (
            <ExpenseCard
              key={expense.payment?.id || expense.id}
              expense={expense}
            />
          ))}
        </div>
      )}

      {/* Add Expense Form Modal */}
      <AddExpenseForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setPreSelectedAsset(null);
        }}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        assets={assets}
        selectedAsset={preSelectedAsset}
      />
    </div>
  );
};

export default AssetExpenses;
